import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, CalendarHeart, Menu, X, Globe, Leaf } from "lucide-react";
import { useI18n } from "../../i18n/I18nContext";
import { Button } from "../ui/button";

const PHONE = "450-736-0748";

export default function Header() {
  const { t, lang, toggle } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/la-residence", label: t("nav.residence") },
    { to: "/services", label: t("nav.services") },
    { to: "/milieu-de-vie", label: t("nav.milieu") },
    { to: "/admission", label: t("nav.admission") },
    { to: "/galerie", label: t("nav.gallery") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/carrieres", label: t("nav.careers") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const linkClass = ({ isActive }) =>
    `px-2 py-2 text-[15px] font-medium transition-colors border-b-2 ${
      isActive ? "text-[#2A9D8F] border-[#2A9D8F]" : "text-[#2D3142] border-transparent hover:text-[#2A9D8F]"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-[#F9F6F0]/95 backdrop-blur border-b border-black/5" data-testid="site-header">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3 sm:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link" aria-label="Résidence Doux Instants">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#2A9D8F]">
            <Leaf className="h-6 w-6 text-white" />
          </span>
          <span className="leading-tight">
            <span className="block font-display text-lg text-[#1E293B]">Résidence</span>
            <span className="block font-display text-lg font-semibold text-[#2A9D8F] -mt-1">Doux Instants</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1" aria-label="Navigation principale">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} data-testid={`nav-${l.to}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2">
          <button onClick={toggle} data-testid="lang-toggle"
            className="flex items-center gap-1 rounded-full border border-[#2A9D8F]/40 px-3 py-2 text-sm font-semibold text-[#2A9D8F] transition-colors hover:bg-[#2A9D8F]/10"
            aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}>
            <Globe className="h-4 w-4" /> {lang === "fr" ? "EN" : "FR"}
          </button>
          <a href={`tel:${PHONE}`} data-testid="header-call"
            className="flex min-h-[44px] items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold text-[#2D3142] hover:text-[#2A9D8F]">
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <Button asChild className="rounded-full bg-[#2A9D8F] hover:bg-[#21867A] min-h-[44px]">
            <Link to="/contact" data-testid="header-plan-visit">
              <CalendarHeart className="mr-2 h-4 w-4" /> {t("common.planVisit")}
            </Link>
          </Button>
        </div>

        <button className="xl:hidden inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-[#2D3142]"
          onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Menu" data-testid="mobile-menu-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden border-t border-black/5 bg-[#F9F6F0]" data-testid="mobile-menu">
          <nav className="mx-auto max-w-6xl px-5 py-3" aria-label="Navigation mobile">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`mobile-nav-${l.to}`}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-3 text-base font-medium ${isActive ? "bg-[#2A9D8F]/10 text-[#2A9D8F]" : "text-[#2D3142]"}`}>
                {l.label}
              </NavLink>
            ))}
            <button onClick={toggle} data-testid="mobile-lang-toggle"
              className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-3 text-base font-semibold text-[#2A9D8F]">
              <Globe className="h-5 w-5" /> {lang === "fr" ? "English" : "Français"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
